import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ChallengeAssessment from './components/ChallengeAssessment';
import RecommendationEngine from './components/RecommendationEngine';
import QualificationForm from './components/QualificationForm';
import CalendarIntegration from './components/CalendarIntegration';
import ResourceLibrary from './components/ResourceLibrary';
import TrustSignals from './components/TrustSignals';

const DiscoveryPortal = () => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState('welcome');
  const [assessmentData, setAssessmentData] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [formData, setFormData] = useState(null);

  const steps = {
    welcome: t('discoveryPortal.journey.steps.welcome'),
    assessment: t('discoveryPortal.journey.steps.assessment.title'),
    recommendations: t('discoveryPortal.journey.steps.recommendations.title'),
    qualification: t('discoveryPortal.journey.steps.qualification.title'),
    calendar: t('discoveryPortal.journey.steps.discoveryCall.title'),
    resources: t('discoveryPortal.resources.title'),
    trust: t('discoveryPortal.trust.title')
  };

  const handleAssessmentComplete = (data) => {
    setAssessmentData(data);
    setCurrentStep('recommendations');
  };

  const handleScheduleCall = (service) => {
    setSelectedService(service);
    setCurrentStep('qualification');
  };

  const handleQualificationSubmit = (data) => {
    setFormData(data);
    setCurrentStep('calendar');
  };

  const handleBookingComplete = (bookingData) => {
    console.log('Booking completed:', bookingData);
    // Here you would typically send the data to your backend
    alert('Discovery call scheduled successfully! You will receive a confirmation email shortly.');
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 'welcome':
        return (
          <div className="max-w-4xl mx-auto text-center space-y-8">
            {/* Hero Section */}
            <div className="space-y-6">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-accent/10 rounded-full mb-6">
                <Icon name="Compass" size={40} color="var(--color-accent)" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-text-primary">
                {t('discoveryPortal.title')}
              </h1>
              <p className="text-xl text-text-secondary max-w-3xl mx-auto">
                {t('discoveryPortal.description')}
              </p>
            </div>

            {/* Value Proposition */}
            <div className="grid md:grid-cols-3 gap-8 my-12">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-accent/10 rounded-full mb-4">
                  <Icon name="Target" size={32} color="var(--color-accent)" />
                </div>
                <h3 className="text-lg font-semibold text-text-primary mb-2">
                  {t('discoveryPortal.valueProps.intelligentAssessment.title')}
                </h3>
                <p className="text-text-secondary">
                  {t('discoveryPortal.valueProps.intelligentAssessment.description')}
                </p>
              </div>
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-accent/10 rounded-full mb-4">
                  <Icon name="Lightbulb" size={32} color="var(--color-accent)" />
                </div>
                <h3 className="text-lg font-semibold text-text-primary mb-2">
                  {t('discoveryPortal.valueProps.personalizedRecommendations.title')}
                </h3>
                <p className="text-text-secondary">
                  {t('discoveryPortal.valueProps.personalizedRecommendations.description')}
                </p>
              </div>
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-accent/10 rounded-full mb-4">
                  <Icon name="Users" size={32} color="var(--color-accent)" />
                </div>
                <h3 className="text-lg font-semibold text-text-primary mb-2">
                  {t('discoveryPortal.valueProps.expertConsultation.title')}
                </h3>
                <p className="text-text-secondary">
                  {t('discoveryPortal.valueProps.expertConsultation.description')}
                </p>
              </div>
            </div>

            {/* Process Overview */}
            <div className="bg-card rounded-lg border border-border p-8">
              <h2 className="text-2xl font-semibold text-text-primary mb-6">
                {t('discoveryPortal.journey.title')}
              </h2>
              <div className="grid md:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="w-8 h-8 bg-accent text-white rounded-full flex items-center justify-center mx-auto mb-3 text-sm font-semibold">
                    1
                  </div>
                  <h4 className="font-medium text-text-primary mb-2">{t('discoveryPortal.journey.steps.assessment.title')}</h4>
                  <p className="text-sm text-text-secondary">
                    {t('discoveryPortal.journey.steps.assessment.description')}
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-8 h-8 bg-accent text-white rounded-full flex items-center justify-center mx-auto mb-3 text-sm font-semibold">
                    2
                  </div>
                  <h4 className="font-medium text-text-primary mb-2">{t('discoveryPortal.journey.steps.recommendations.title')}</h4>
                  <p className="text-sm text-text-secondary">
                    {t('discoveryPortal.journey.steps.recommendations.description')}
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-8 h-8 bg-accent text-white rounded-full flex items-center justify-center mx-auto mb-3 text-sm font-semibold">
                    3
                  </div>
                  <h4 className="font-medium text-text-primary mb-2">{t('discoveryPortal.journey.steps.qualification.title')}</h4>
                  <p className="text-sm text-text-secondary">
                    {t('discoveryPortal.journey.steps.qualification.description')}
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-8 h-8 bg-accent text-white rounded-full flex items-center justify-center mx-auto mb-3 text-sm font-semibold">
                    4
                  </div>
                  <h4 className="font-medium text-text-primary mb-2">{t('discoveryPortal.journey.steps.discoveryCall.title')}</h4>
                  <p className="text-sm text-text-secondary">
                    {t('discoveryPortal.journey.steps.discoveryCall.description')}
                  </p>
                </div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="default"
                size="lg"
                className="bg-accent hover:bg-accent/90"
                iconName="Play"
                iconPosition="left"
                onClick={() => setCurrentStep('assessment')}
              >
                {t('discoveryPortal.cta.startAssessment')}
              </Button>
              <Button
                variant="outline"
                size="lg"
                iconName="BookOpen"
                iconPosition="left"
                onClick={() => setCurrentStep('resources')}
              >
                {t('discoveryPortal.cta.browseResources')}
              </Button>
            </div>
          </div>
        );

      case 'assessment':
        return (
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-text-primary mb-2">
                {t('discoveryPortal.assessment.title')}
              </h1>
              <p className="text-text-secondary">
                {t('discoveryPortal.assessment.description')}
              </p>
            </div>
            <ChallengeAssessment onComplete={handleAssessmentComplete} />
          </div>
        );

      case 'recommendations':
        return (
          <div className="max-w-6xl mx-auto">
            <RecommendationEngine 
              assessmentData={assessmentData} 
              onScheduleCall={handleScheduleCall}
            />
          </div>
        );

      case 'qualification':
        return (
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-text-primary mb-2">
                {t('discoveryPortal.qualification.title')}
              </h1>
              <p className="text-text-secondary">
                {t('discoveryPortal.qualification.description')}
              </p>
            </div>
            <QualificationForm onSubmit={handleQualificationSubmit} />
          </div>
        );

      case 'calendar':
        return (
          <div className="max-w-6xl mx-auto">
            <CalendarIntegration 
              selectedService={selectedService}
              onBooking={handleBookingComplete}
            />
          </div>
        );

      case 'resources':
        return (
          <div className="max-w-6xl mx-auto">
            <ResourceLibrary />
          </div>
        );

      case 'trust':
        return (
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-text-primary mb-2">
                {t('discoveryPortal.trust.title')}
              </h1>
              <p className="text-text-secondary">
                {t('discoveryPortal.trust.description')}
              </p>
            </div>
            <TrustSignals />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Navigation Breadcrumb */}
      {currentStep !== 'welcome' && (
        <div className="bg-muted border-b border-border">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="ArrowLeft"
                  iconPosition="left"
                  onClick={() => {
                    if (currentStep === 'assessment') setCurrentStep('welcome');
                    else if (currentStep === 'recommendations') setCurrentStep('assessment');
                    else if (currentStep === 'qualification') setCurrentStep('recommendations');
                    else if (currentStep === 'calendar') setCurrentStep('qualification');
                    else setCurrentStep('welcome');
                  }}
                >
                  {t('back')}
                </Button>
                <div className="flex items-center space-x-2 text-sm text-text-secondary">
                  <span>{t('discoveryPortal.title')}</span>
                  <Icon name="ChevronRight" size={16} />
                  <span className="text-text-primary font-medium">{steps[currentStep]}</span>
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="BookOpen"
                  iconPosition="left"
                  onClick={() => setCurrentStep('resources')}
                >
                  {t('discoveryPortal.cta.browseResources')}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Shield"
                  iconPosition="left"
                  onClick={() => setCurrentStep('trust')}
                >
                  Security
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="py-12 px-6 lg:px-8">
        {renderStepContent()}
      </main>

      {/* Footer */}
      <footer className="bg-card border-t border-border py-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex items-center space-x-6 mb-4 md:mb-0">
              <div className="flex items-center space-x-2">
                <Icon name="Shield" size={16} color="var(--color-success)" />
                <span className="text-sm text-text-secondary">{t('discoveryPortal.footer.enterpriseSecurity')}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Clock" size={16} color="var(--color-success)" />
                <span className="text-sm text-text-secondary">{t('discoveryPortal.footer.freeDiscoveryCall')}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="FileText" size={16} color="var(--color-success)" />
                <span className="text-sm text-text-secondary">{t('discoveryPortal.footer.ndaAvailable')}</span>
              </div>
            </div>
            <div className="text-sm text-text-secondary">
              © {new Date().getFullYear()} SIGNA Technologies. {t('footer.allRightsReserved')}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DiscoveryPortal;